import { Async, toAsyncFetched } from '@client/new/helpers/types';
import { Discount, DiscountsModel, useDiscountsModel } from './Discounts.model';
import { useEffect, useState } from 'react';
import { useAsyncResult } from '@client/new/helpers/gql';
import {
  toDiscountUsers,
  toDiscountPerformance,
  toDiscount,
  toDisabledCheckoutLink,
} from './DiscountDetails.mapper';
import {
  GetCouponDetailsQuery,
  useGetCouponDetailsQuery,
} from 'generated/graphql';
import { useCreator } from '@client/new/common/LiteUser.mapper';

export type DiscountUser = {
  purchaseDate: string;
  name: string;
  email: string;
  userType:
    | 'Active Subscriber'
    | 'New'
    | 'Returning'
    | 'Returning OTP'
    | 'Upgrading';
};

export type DiscountPerformance = {
  totalRedemptions: number;
  totalConversion?: number;
  totalConversionPercent?: number;
  discountedPrice: number;
};

export type DiscountDetailsModel = {
  performance: DiscountPerformance;
  users: DiscountUser[];
  discount: Discount;
  disableCheckoutLink: boolean;
} & Pick<DiscountsModel, 'deactivateDiscount'>;

export const useDiscountDetailsModel = (
  discountId: string,
): Async<DiscountDetailsModel> => {
  const discountsModel = useDiscountsModel();
  const res = useDiscountDetails(discountId, discountsModel);
  const creator = useCreator();

  if (res.data.type !== 'fetched') return res.data;
  if (discountsModel.type !== 'fetched') return discountsModel;
  if (creator.type !== 'fetched') return creator;

  const details = res.data.value;
  const discount = toDiscount(details, creator.value.customURL);
  return toAsyncFetched({
    discount,
    users: toDiscountUsers(
      details.redemptionsList || [],
      discount.product.type,
    ),
    performance: toDiscountPerformance(discount, details),
    deactivateDiscount: discountsModel.value.deactivateDiscount,
    disableCheckoutLink: toDisabledCheckoutLink(details),
  });
};

const useDiscountDetails = (
  discountId: string,
  discountsModel: Async<DiscountsModel>,
) => {
  const q = useGetCouponDetailsQuery({
    variables: { discountId: parseInt(discountId, 10) },
  });

  const [data, setData] = useState<
    Async<GetCouponDetailsQuery['getCouponDetails']>
  >({
    type: 'not-fetched',
  });
  const res = useAsyncResult<GetCouponDetailsQuery>(q);
  useEffect(() => {
    if (res.type !== 'fetched') setData(res);
    else if (discountsModel.type !== 'fetched') setData(discountsModel);
    else {
      const value = res.value.getCouponDetails;
      const d = toAsyncFetched(value);
      if (JSON.stringify(d) === JSON.stringify(data)) return;
      setData(d);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [res.type, discountsModel]);

  const refetch = () => {
    setData({ type: 'fetching' });
    q.refetch()
      .then((v) => {
        setData(toAsyncFetched(v.data.getCouponDetails));
      })
      .catch((err) => setData({ type: 'fetch-error', value: err }));
  };

  return { refetch, data };
};
